import Recaptcha from "./Recaptcha";
import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import i18n from "i18next";
import util from "../lib/chart_functions";
import {FiMail, FiCheckCircle, FiXCircle, FiX} from "react-icons/fi";
import './FilterPanel.css';


class PopupForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            showPopup: false,
            captchaReady: false,
            captchaToken: '',
            captchaReset: 0,
            response: '[]',
            status_code: 200,
            isSubmitting: false,
            notification: null,  // { type: 'success' | 'error', message: string }
            // V2 API fields - Contact Information
            full_name: '',
            position: '',
            organization: '',
            email: '',
            phone: '',
            // V2 API fields - Donor Information
            primary_diagnosis: '',
            additional_diagnosis: '',
            sex: '',
            age: '',
            required_donor_info: '',
            additional_donor_params: '',
            // V2 API fields - Sample Characteristics
            biomaterial_type: '',
            sample_count: '',
            min_sample_volume: '',
            required_sample_tests: '',
            additional_sample_params: '',
            // Legacy
            query: this.props.query,
            object_type: this.props.object_type
        };
    }

    componentDidMount() {
        window.addEventListener('keydown', this.handleEscKey);
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleEscKey);
    }

    handleEscKey = (e) => {
        if (e.key !== 'Escape') return;
        // Notification first — it's the top layer in the stack.
        if (this.state.notification) {
            this.closeNotification();
        } else if (this.state.showPopup) {
            this.setState({ showPopup: false });
        }
    };

    togglePopup = () => {
        const query = this.state.query || {};
        const display_query = util.my_stringify(query);

        // Pre-fill fields from query if available
        // Sex
        const sexValue = query.sex && Array.isArray(query.sex) && query.sex.length > 0
            ? query.sex.join(', ')
            : '';

        // Age with age_settings (NA, newborn)
        let ageValue = '';
        const ageSettings = [];
        if (query.age && Array.isArray(query.age) && query.age.length === 2) {
            ageValue = `${query.age[0]}-${query.age[1]}`;
        }
        if (query.age_settings && Array.isArray(query.age_settings) && query.age_settings.length > 0) {
            query.age_settings.forEach(setting => {
                if (setting === 'age_na') {
                    ageSettings.push(i18n.t('age_settings_age_na'));
                } else if (setting === 'age_nb') {
                    ageSettings.push(i18n.t('age_settings_age_nb'));
                }
            });
        }
        if (ageSettings.length > 0) {
            ageValue = ageValue ? `${ageValue}; ${ageSettings.join(', ')}` : ageSettings.join(', ');
        }

        // Type (biomaterial) - comma separated, translated
        let typeValue = '';
        if (query.type && Array.isArray(query.type) && query.type.length > 0) {
            typeValue = query.type.map(t => i18n.t(`type_${t}`) || t).join(', ');
        }

        // Organization and Collection for additional_donor_params
        let additionalDonorParams = '';
        const donorParamsParts = [];

        if (query.organization && Array.isArray(query.organization) && query.organization.length > 0) {
            const orgTranslated = query.organization.map(o => i18n.t(`organization_${o}`) || o).join(', ');
            donorParamsParts.push(`${i18n.t('filter_organization')}: ${orgTranslated}`);
        }

        if (query.collection && Array.isArray(query.collection) && query.collection.length > 0) {
            const collTranslated = query.collection.map(c => i18n.t(`collection_${c}`) || c).join(', ');
            donorParamsParts.push(`${i18n.t('filter_collection')}: ${collTranslated}`);
        }

        additionalDonorParams = donorParamsParts.join('\n');

        this.setState({
            ...this.state,
            display_query: display_query,
            sex: sexValue,
            age: ageValue,
            biomaterial_type: typeValue,
            additional_donor_params: additionalDonorParams,
            "showPopup": !this.state.showPopup
        })
    };

    handleChange = (e) => {
        this.setState({
            ...this.state,
            [e.target.name]: e.target.value
        });
    };

    closeNotification = () => {
        this.setState({ notification: null });
    }

    handleCaptcha = ({ready, token}) => this.setState({captchaReady: ready, captchaToken: token});

    isFormValid = () => {
        const requiredFields = [
            'full_name',
            'position',
            'organization',
            'email',
            'phone',
            'biomaterial_type',
            'sample_count',
            'min_sample_volume'
        ];
        const requiredFieldsFilled = requiredFields.every(field =>
            String(this.state[field] || '').trim().length > 0
        );
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        return requiredFieldsFilled && emailRegex.test(this.state.email.trim());
    };

    createNewRequest = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        // The UI keeps this action disabled until the form is valid. Retain
        // the guard for programmatic calls and rapid repeated interactions.
        if (!this.isFormValid() || !this.state.captchaReady || this.state.isSubmitting) return;

        this.setState({isSubmitting: true});

        // Prepare request data for V2 API
        const requestData = {
            captcha_token: this.state.captchaToken,
            // Contact Information
            full_name: this.state.full_name,
            position: this.state.position,
            organization: this.state.organization,
            email: this.state.email,
            phone: this.state.phone,
            // Donor Information
            primary_diagnosis: this.state.primary_diagnosis,
            additional_diagnosis: this.state.additional_diagnosis,
            sex: this.state.sex,
            age: this.state.age,
            required_donor_info: this.state.required_donor_info,
            additional_donor_params: this.state.additional_donor_params,
            // Sample Characteristics
            biomaterial_type: this.state.biomaterial_type,
            sample_count: this.state.sample_count,
            min_sample_volume: this.state.min_sample_volume,
            required_sample_tests: this.state.required_sample_tests,
            additional_sample_params: this.state.additional_sample_params
        };

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(requestData)
        };

        fetch(`${process.env.REACT_APP_BACK_END}/request/v2/`, requestOptions)
            .then(async response => {
                const statusCode = response.status;
                this.setState({status_code: statusCode});
                const data = await response.json();
                return {response, data};
            })
            .then(({response, data}) => {
                const emailsDelivered = data.notifications
                    ? data.notifications.user_email && data.notifications.admin_email
                    : true;

                if (response.ok && data.status === 'success' && emailsDelivered) {
                    this.setState({
                        showPopup: false,
                        isSubmitting: false,
                        notification: { type: 'success', message: i18n.t('request_v2_success') }
                    });
                    this.resetForm();
                } else {
                    this.setState({
                        isSubmitting: false,
                        notification: {
                            type: 'error',
                            message: data.code?.startsWith("captcha_") ? i18n.t(data.code) : data.message || i18n.t('request_v2_error')
                        }
                    });
                }
            })
            .catch(error => {
                console.error('Request error:', error);
                this.setState({
                    isSubmitting: false,
                    notification: { type: 'error', message: i18n.t('request_v2_error') + ': ' + error.message }
                });
            })
            .finally(() => this.setState(state => ({captchaReset: state.captchaReset + 1})));
    };

    resetForm = () => {
        this.setState({
            full_name: '',
            position: '',
            organization: '',
            email: '',
            phone: '',
            primary_diagnosis: '',
            additional_diagnosis: '',
            sex: '',
            age: '',
            required_donor_info: '',
            additional_donor_params: '',
            biomaterial_type: '',
            sample_count: '',
            min_sample_volume: '',
            required_sample_tests: '',
            additional_sample_params: ''
        });
    };

    renderNotification = () => {
        const { notification } = this.state;
        if (!notification) return null;

        const isSuccess = notification.type === 'success';

        return ReactDOM.createPortal(
            <div className="notification-overlay" onClick={this.closeNotification}>
                <div
                    className="notification-modal"
                    onClick={e => e.stopPropagation()}
                    role="alertdialog"
                    aria-modal="true"
                    aria-labelledby="notification-title"
                    aria-describedby="notification-message"
                >
                    <button
                        className="notification-close"
                        onClick={this.closeNotification}
                        aria-label={i18n.t('close')}
                    >
                        <FiX size={20} />
                    </button>
                    <div className={`notification-icon ${isSuccess ? 'success' : 'error'}`}>
                        {isSuccess ? <FiCheckCircle size={48} /> : <FiXCircle size={48} />}
                    </div>
                    <h3 id="notification-title" className="notification-title">
                        {isSuccess ? i18n.t('notification_success_title') : i18n.t('notification_error_title')}
                    </h3>
                    <p id="notification-message" className="notification-message">{notification.message}</p>
                    <button className="notification-button" onClick={this.closeNotification} autoFocus>
                        {i18n.t('notification_ok_button')}
                    </button>
                </div>
            </div>,
            document.body
        );
    }

    render() {
        const {showPopup, isSubmitting} = this.state;
        const isFormValid = this.isFormValid();
        return (
            <div>
                {this.renderNotification()}
                <button className="filter-request-btn w-100" onClick={this.togglePopup}>
                    <FiMail size={18} />
                    {i18n.t("make_request")}
                </button>

                {showPopup && ReactDOM.createPortal(
                    <div
                        className="modal show mail-request-modal"
                        tabIndex="-1"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="mail-request-title"
                    >
                        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                            <div className="modal-content">
                                <div className="mail-request-header">
                                    <h2 id="mail-request-title" className="mail-request-title">{i18n.t("request_v2_title")}</h2>
                                    <button
                                        type="button"
                                        className="mail-request-close"
                                        onClick={this.togglePopup}
                                        aria-label={i18n.t('close')}
                                    >
                                        <FiX size={20} />
                                    </button>
                                </div>
                                <div className="modal-body mail-request-body">
                                    <div className="needs-validation">
                                        {/* Contact Information Section */}
                                        <h6 className="mail-request-section-title">{i18n.t("request_v2_section_contact")}</h6>

                                        <div className="mb-3">
                                            <label htmlFor="full_name" className="form-label">
                                                {i18n.t("request_v2_full_name")} <span className="text-danger">*</span>
                                            </label>
                                            <input type="text" className="form-control" id="full_name"
                                                   name="full_name" value={this.state.full_name}
                                                   onChange={this.handleChange} required />
                                        </div>

                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="position" className="form-label">
                                                    {i18n.t("request_v2_position")} <span className="text-danger">*</span>
                                                </label>
                                                <input type="text" className="form-control" id="position"
                                                       name="position" value={this.state.position}
                                                       onChange={this.handleChange} required />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="organization" className="form-label">
                                                    {i18n.t("request_v2_organization")} <span className="text-danger">*</span>
                                                </label>
                                                <input type="text" className="form-control" id="organization"
                                                       name="organization" value={this.state.organization}
                                                       onChange={this.handleChange} required />
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="email" className="form-label">
                                                    {i18n.t("request_email")} <span className="text-danger">*</span>
                                                </label>
                                                <input type="email" className="form-control" id="email"
                                                       name="email" value={this.state.email}
                                                       onChange={this.handleChange} required />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="phone" className="form-label">
                                                    {i18n.t("request_phone")} <span className="text-danger">*</span>
                                                </label>
                                                <input type="tel" className="form-control" id="phone"
                                                       name="phone" value={this.state.phone}
                                                       onChange={this.handleChange} required />
                                            </div>
                                        </div>

                                        {/* Donor Information Section */}
                                        <h6 className="mail-request-section-title mail-request-section-title--spaced">{i18n.t("request_v2_section_donor")}</h6>

                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="primary_diagnosis" className="form-label">
                                                    {i18n.t("request_v2_primary_diagnosis")}
                                                </label>
                                                <input type="text" className="form-control" id="primary_diagnosis"
                                                       name="primary_diagnosis" value={this.state.primary_diagnosis}
                                                       onChange={this.handleChange}/>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="additional_diagnosis" className="form-label">
                                                    {i18n.t("request_v2_additional_diagnosis")}
                                                </label>
                                                <input type="text" className="form-control" id="additional_diagnosis"
                                                       name="additional_diagnosis" value={this.state.additional_diagnosis}
                                                       onChange={this.handleChange}/>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="sex" className="form-label">
                                                    {i18n.t("request_v2_sex")}
                                                </label>
                                                <input type="text" className="form-control" id="sex"
                                                       name="sex" value={this.state.sex}
                                                       onChange={this.handleChange}
                                                       placeholder="M, F"/>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="age" className="form-label">
                                                    {i18n.t("request_v2_age")}
                                                </label>
                                                <input type="text" className="form-control" id="age"
                                                       name="age" value={this.state.age}
                                                       onChange={this.handleChange}
                                                       placeholder="25-45"/>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label htmlFor="required_donor_info" className="form-label">
                                                {i18n.t("request_v2_required_donor_info")}
                                            </label>
                                            <textarea className="form-control" id="required_donor_info"
                                                      name="required_donor_info" value={this.state.required_donor_info}
                                                      onChange={this.handleChange} rows="2"></textarea>
                                        </div>

                                        <div className="mb-3">
                                            <label htmlFor="additional_donor_params" className="form-label">
                                                {i18n.t("request_v2_additional_donor_params")}
                                            </label>
                                            <textarea className="form-control" id="additional_donor_params"
                                                      name="additional_donor_params" value={this.state.additional_donor_params}
                                                      onChange={this.handleChange} rows="2"></textarea>
                                        </div>

                                        {/* Sample Characteristics Section */}
                                        <h6 className="mail-request-section-title mail-request-section-title--spaced">{i18n.t("request_v2_section_sample")}</h6>

                                        <div className="row">
                                            <div className="col-md-4 mb-3">
                                                <label htmlFor="biomaterial_type" className="form-label">
                                                    {i18n.t("request_v2_biomaterial_type")} <span className="text-danger">*</span>
                                                </label>
                                                <input type="text" className="form-control" id="biomaterial_type"
                                                       name="biomaterial_type" value={this.state.biomaterial_type}
                                                       onChange={this.handleChange} required />
                                            </div>
                                            <div className="col-md-4 mb-3">
                                                <label htmlFor="sample_count" className="form-label">
                                                    {i18n.t("request_v2_sample_count")} <span className="text-danger">*</span>
                                                </label>
                                                <input type="text" className="form-control" id="sample_count"
                                                       name="sample_count" value={this.state.sample_count}
                                                       onChange={this.handleChange} required />
                                            </div>
                                            <div className="col-md-4 mb-3">
                                                <label htmlFor="min_sample_volume" className="form-label">
                                                    {i18n.t("request_v2_min_sample_volume")} <span className="text-danger">*</span>
                                                </label>
                                                <input type="text" className="form-control" id="min_sample_volume"
                                                       name="min_sample_volume" value={this.state.min_sample_volume}
                                                       onChange={this.handleChange} required />
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label htmlFor="required_sample_tests" className="form-label">
                                                {i18n.t("request_v2_required_sample_tests")}
                                            </label>
                                            <textarea className="form-control" id="required_sample_tests"
                                                      name="required_sample_tests" value={this.state.required_sample_tests}
                                                      onChange={this.handleChange} rows="2"></textarea>
                                        </div>

                                        <div className="mb-3">
                                            <label htmlFor="additional_sample_params" className="form-label">
                                                {i18n.t("request_v2_additional_sample_params")}
                                            </label>
                                            <textarea className="form-control" id="additional_sample_params"
                                                      name="additional_sample_params" value={this.state.additional_sample_params}
                                                      onChange={this.handleChange} rows="2"></textarea>
                                        </div>

                                        <Recaptcha onChange={this.handleCaptcha} resetVersion={this.state.captchaReset} />
                      <div className="mail-request-actions">
                                            <button type="button" className="mail-request-cancel-btn" onClick={this.togglePopup}>
                                                {i18n.t("request_cancel")}
                                            </button>
                                            <button
                                                type="button"
                                                className="mail-request-submit-btn"
                                                onClick={this.createNewRequest}
                                                disabled={!isFormValid || !this.state.captchaReady || isSubmitting}
                                            >
                                                {i18n.t(isSubmitting ? "request_v2_sending" : "make_request")}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        );
    }
}


export {PopupForm};
